import React, { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { Shimmer } from "../components/MotionPrimitives";
import NewTicketModal from "../components/NewTicketModal";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import useImpersonationGuard from "../hooks/useImpersonationGuard";
import apiService from "../services/api";

const Tickets = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const { guardImpersonatedAction, isImpersonating } = useImpersonationGuard();
  const { markAsReadByTicketId } = useNotifications();

  // Helper function to get relative time
  const getRelativeTime = (date) => {
    if (!date) return "No replies";

    const now = new Date();
    const then = new Date(date);
    const diff = now - then;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years} year${years > 1 ? "s" : ""} ago`;
    if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (weeks > 0) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (seconds > 0) return `${seconds} second${seconds > 1 ? "s" : ""} ago`;

    return "Just now";
  };

  // Check if we should filter by assigned tickets or registration_id
  const queryParams = new URLSearchParams(location.search);
  const filterParam = queryParams.get("filter");
  const registrationIdParam = queryParams.get("registration_id");
  const [filter, setFilter] = useState(
    filterParam === "assigned" ? "assigned" : "all"
  );
  const [registrationIdFilter, setRegistrationIdFilter] = useState(
    registrationIdParam || ""
  );
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [newTicketInitialSubject, setNewTicketInitialSubject] = useState("");
  const [sortField, setSortField] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' or 'desc'
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    // For admins, default to showing "open" and "customer_replied"
    // For customers, default to empty array (show all)
    user?.role === "admin" ? ["open", "customer_replied"] : []
  );
  const [showCreatedByAdmin, setShowCreatedByAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedTickets, setSelectedTickets] = useState(() => new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1,
  });

  // Update filter when URL changes
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const filterParam = queryParams.get("filter");
    const registrationIdParam = queryParams.get("registration_id");
    const actionParam = queryParams.get("action");
    const subjectParam = queryParams.get("subject");

    setFilter(filterParam === "assigned" ? "assigned" : "all");
    setRegistrationIdFilter(registrationIdParam || "");

    if (actionParam === "new") {
      setNewTicketInitialSubject(subjectParam?.trim() || "");
      setShowNewTicketModal(true);
      const newParams = new URLSearchParams(location.search);

      newParams.delete("action");
      newParams.delete("subject");
      const newUrl = newParams.toString()
        ? `?${newParams.toString()}`
        : location.pathname;

      window.history.replaceState({}, "", newUrl);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    fetchTickets();
  }, [
    token,
    filter,
    pageSize,
    sortField,
    currentPage,
    sortDirection,
    registrationIdFilter,
    selectedStatuses,
    showCreatedByAdmin,
  ]);

  // Subtle refresh tickets when page gains focus (without full reload)
  useEffect(() => {
    const handleFocus = async () => {
      // Only do a subtle background refresh if not currently loading
      if (!loading && !isRefreshing) {
        setIsRefreshing(true);
        try {
          const params = {
            sort:
              sortField === "createdAt"
                ? "created_at"
                : sortField === "updatedAt"
                  ? "updated_at"
                  : sortField,
            order: sortDirection.toUpperCase(),
            page: currentPage,
            limit: pageSize,
          };

          if (registrationIdFilter) {
            params.registration_id = registrationIdFilter;
          }

          // Add status filter (comma-separated for multiple)
          if (selectedStatuses.length > 0) {
            params.status = selectedStatuses.join(",");
          }

          // Add created_by_admin filter
          if (showCreatedByAdmin) {
            params.created_by_admin = "true";
          }

          const data = await apiService.getTickets(params);

          // Transform and update tickets without showing loading state
          const transformedTickets = data.tickets.map(transformTicket);

          setTickets(transformedTickets);
          if (data.pagination) {
            setPagination(data.pagination);
          }
        } catch (error) {
          // Silently handle error
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    let lastFocusTime = Date.now();
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const timeSinceLastFocus = Date.now() - lastFocusTime;

        if (timeSinceLastFocus > 30000) {
          handleFocus();
          lastFocusTime = Date.now();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    const refreshInterval = setInterval(() => {
      if (!document.hidden && !loading && !isRefreshing) {
        handleFocus();
      }
    }, 60000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [
    loading,
    pageSize,
    sortField,
    currentPage,
    isRefreshing,
    sortDirection,
    registrationIdFilter,
    selectedStatuses,
    showCreatedByAdmin,
  ]);

  const handleTicketCreated = () => {
    // Refresh tickets list
    fetchTickets();
  };

  const handleCloseNewTicketModal = () => {
    setShowNewTicketModal(false);
    setNewTicketInitialSubject("");
  };

  const handleTicketClick = async (ticketNumber) => {
    // Mark notifications for this ticket as read
    await markAsReadByTicketId(ticketNumber);

    // Navigate to ticket detail
    navigate(`/tickets/${ticketNumber}`);
  };

  // Helper function to transform ticket data
  const transformTicket = (ticket) => {
    // Get the last reply time from comments or use updated_at
    let lastReplyTime = ticket.updated_at || ticket.created_at;
    let lastReplyBy = null;
    let hasCustomerReply = false;

    // Check if there are comments to determine actual last reply
    // Comments might be an object with a 'data' array or just a number count
    let commentsArray = [];

    if (ticket.comments) {
      if (Array.isArray(ticket.comments)) {
        commentsArray = ticket.comments;
      } else if (ticket.comments.data && Array.isArray(ticket.comments.data)) {
        commentsArray = ticket.comments.data;
      }
    }

    if (commentsArray.length > 0) {
      // Get the most recent comment
      const sortedComments = [...commentsArray].sort(
        (a, b) =>
          new Date(b.created_at || b.createdAt) -
          new Date(a.created_at || a.createdAt)
      );
      const lastComment = sortedComments[0];

      if (lastComment) {
        lastReplyTime = lastComment.created_at || lastComment.createdAt;
        lastReplyBy = lastComment.user;
        hasCustomerReply = lastReplyBy?.role !== "admin";
      }
    }
    // If no comments, hasCustomerReply stays false (no replies yet)

    return {
      id: ticket.ticket_number || ticket.ticket_id,
      subject: ticket.subject,
      description: ticket.description,
      customer: ticket.customer?.name || ticket.customer?.email || "Unknown",
      customerId: ticket.customer?.id,
      customerType: ticket.customer?.type,
      customerAvatarUrl: ticket.customer?.avatarUrl || null,
      status: ticket.status.replace("_", "-"), // Convert customer_replied to customer-replied
      category: ticket.category,
      createdAt: ticket.created_at,
      updatedAt: ticket.updated_at || ticket.created_at,
      lastReplyTime,
      lastReplyBy,
      hasCustomerReply,
      assignedTo: ticket.assignedTo?.name || ticket.assignedTo?.email || null, // Prioritize name over email
      assignedToId: ticket.assignedTo?.id,
      assignedToEmail: ticket.assignedTo?.email,
      assignedToObject: ticket.assignedTo, // Keep the full object for filtering
      assignedToAvatarUrl: ticket.assignedTo?.avatarUrl || null,
      commentCount: ticket.comments?.count || ticket.comments?.length || 0,
      comments: commentsArray,
      attachments: ticket.attachments || [],
      metadata: ticket.metadata || {},
    };
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        sort:
          sortField === "createdAt"
            ? "created_at"
            : sortField === "updatedAt"
              ? "updated_at"
              : sortField,
        order: sortDirection.toUpperCase(),
        page: currentPage,
        limit: pageSize,
      };

      // Add registration_id filter if present
      if (registrationIdFilter) {
        params.registration_id = registrationIdFilter;
      }

      // Add status filter (comma-separated for multiple)
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses.join(",");
      }

      // Add created_by_admin filter
      if (showCreatedByAdmin) {
        params.created_by_admin = "true";
      }

      const data = await apiService.getTickets(params);

      // Transform the API response to match frontend expectations
      const transformedTickets = data.tickets.map(transformTicket);

      setTickets(transformedTickets);
      if (data.pagination) {
        setPagination(data.pagination);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Silently handle error - could add toast notification here
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    // Map updatedAt to handle sorting properly
    if (field === "updatedAt") {
      field = "updatedAt";
    }
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Handle status pill toggle
  const toggleStatus = (status) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Remove status if already selected
        return prev.filter((s) => s !== status);
      } else {
        // Add status if not selected
        return [...prev, status];
      }
    });
  };

  // Available status options - conditional based on user role
  const statusOptions =
    user?.role === "admin"
      ? [
          {
            value: "open",
            label: "Open",
            color: "bg-blue-500 hover:bg-blue-600",
          },
          {
            value: "customer_replied",
            label: "Customer Replied",
            color: "bg-orange-500 hover:bg-orange-600",
          },
          {
            value: "answered",
            label: "Answered",
            color: "bg-green-500 hover:bg-green-600",
          },
          {
            value: "closed",
            label: "Closed",
            color:
              "bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800",
          },
        ]
      : [
          // Customers only see open, answered, and closed
          {
            value: "open",
            label: "Open",
            color: "bg-blue-500 hover:bg-blue-600",
          },
          {
            value: "answered",
            label: "Answered",
            color: "bg-green-500 hover:bg-green-600",
          },
          {
            value: "closed",
            label: "Closed",
            color:
              "bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800",
          },
        ];

  // Apply filters in sequence
  let filteredTickets = tickets;

  // 1. Filter by assignment if needed
  if (filter === "assigned" && user) {
    const userEmail = user.email?.toLowerCase();
    const userId = user.id || user.id;

    filteredTickets = filteredTickets.filter((ticket) => {
      // Check if ticket is assigned at all
      if (
        !ticket.assignedTo &&
        !ticket.assignedToId &&
        !ticket.assignedToEmail &&
        !ticket.assignedToObject
      ) {
        return false;
      }

      // Check if assigned to THIS specific user
      const isAssigned =
        // Check ID matches (most reliable)
        ticket.assignedToId === userId ||
        ticket.assignedToObject?.id === userId ||
        ticket.assignedToObject?.id === userId ||
        // Check email matches (case insensitive)
        ticket.assignedToEmail?.toLowerCase() === userEmail ||
        ticket.assignedToObject?.email?.toLowerCase() === userEmail ||
        // Only check assignedTo string if it's an email
        (ticket.assignedTo &&
          ticket.assignedTo.includes("@") &&
          ticket.assignedTo.toLowerCase() === userEmail);

      return isAssigned;
    });
  }

  // 2. Filter by status (multi-select)
  if (selectedStatuses.length > 0) {
    filteredTickets = filteredTickets.filter((ticket) => {
      const ticketStatus = ticket.status?.toLowerCase().replace("-", "_");

      return selectedStatuses.some(
        (status) => ticketStatus === status.toLowerCase().replace("-", "_")
      );
    });
  }

  // 3. Filter by search query
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();

    filteredTickets = filteredTickets.filter(
      (ticket) =>
        ticket.id?.toLowerCase().includes(query) ||
        ticket.subject?.toLowerCase().includes(query) ||
        ticket.customer?.toLowerCase().includes(query) ||
        ticket.assignedTo?.toLowerCase().includes(query) ||
        ticket.category?.toLowerCase().includes(query) ||
        ticket.description?.toLowerCase().includes(query)
    );
  }

  // Use the filtered tickets as-is - the API already sorts them correctly
  // No need for client-side sorting since the backend handles it
  const sortedTickets = filteredTickets;

  const isAdmin = user?.role === "admin";
  const visibleTicketIds = sortedTickets.map(
    (t) => t.ticket_number || t.ticketNumber || t.id
  );
  const allVisibleSelected =
    visibleTicketIds.length > 0 &&
    visibleTicketIds.every((id) => selectedTickets.has(id));

  const toggleAllVisible = () => {
    setSelectedTickets((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        visibleTicketIds.forEach((id) => next.delete(id));
      } else {
        visibleTicketIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const toggleTicket = (ticketId) => {
    setSelectedTickets((prev) => {
      const next = new Set(prev);

      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);

      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (guardImpersonatedAction("Deleting tickets")) return;

    const ids = Array.from(selectedTickets);

    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => apiService.deleteTicket(id))
      );
      const failed = results.filter((r) => r.status === "rejected").length;

      if (failed === 0) {
        toast.success(
          `Deleted ${ids.length} ticket${ids.length === 1 ? "" : "s"}`
        );
      } else if (failed === ids.length) {
        toast.error(`Failed to delete ticket${ids.length === 1 ? "" : "s"}`);
      } else {
        toast.warn(`Deleted ${ids.length - failed} of ${ids.length} tickets`);
      }
      setSelectedTickets(new Set());
      setShowDeleteConfirm(false);
      await fetchTickets();
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Failed to delete tickets");
    } finally {
      setDeleting(false);
    }
  };

  // Don't show the full loading screen, instead show shimmer in table

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Filter indicator */}
      {filter === "assigned" && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fas fa-filter text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
              Showing only tickets assigned to you ({sortedTickets.length}{" "}
              ticket{sortedTickets.length !== 1 ? "s" : ""})
            </span>
          </div>
          <button
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
            onClick={() => {
              setFilter("all");
              setSearchQuery("");
              setSelectedStatuses(
                user?.role === "admin" ? ["open", "customer_replied"] : []
              );
              setShowCreatedByAdmin(false);
              navigate("/tickets");
            }}
          >
            Clear all filters
          </button>
        </div>
      )}

      <div className="w-full flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
        <div className="flex items-center gap-3">
          {isRefreshing && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600" />
              <span>Refreshing...</span>
            </div>
          )}
          <button
            className="btn btn-ghost w-full sm:w-auto"
            onClick={() => {
              setLoading(true);
              fetchTickets();
            }}
          >
            <i className="fas fa-sync-alt mr-2" />
            <span>Refresh</span>
          </button>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {isAdmin && selectedTickets.size > 0 && (
            <button
              className="btn bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
              disabled={deleting}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <i className="fas fa-trash mr-2" />
              <span>Delete ({selectedTickets.size})</span>
            </button>
          )}
          <button
            className="btn btn-primary !rounded-lg w-full sm:w-auto"
            onClick={() => {
              if (guardImpersonatedAction("Creating tickets")) return;

              setNewTicketInitialSubject("");
              setShowNewTicketModal(true);
            }}
          >
            <i className="fas fa-plus mr-2" />
            <span>New Ticket</span>
          </button>
        </div>
      </div>

      {isImpersonating && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          <i className="fas fa-user-shield mt-0.5" />
          <div>
            <p className="font-semibold">
              Ticket actions are locked during impersonation.
            </p>
            <p>
              Return to Admin before creating, deleting, replying to, or closing
              support tickets.
            </p>
          </div>
        </div>
      )}

      <div className="card shadow-md w-full">
        <div className="w-full p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="w-full flex flex-col gap-4">
            {/* Registration filter indicator */}
            {registrationIdFilter && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-filter text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                      Showing tickets for specific customer
                    </span>
                  </div>
                  <button
                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() => {
                      setRegistrationIdFilter("");
                      navigate("/tickets");
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>
            )}

            {/* Results counter */}
            {(searchQuery || selectedStatuses.length > 0) && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Found {sortedTickets.length} ticket
                {sortedTickets.length !== 1 ? "s" : ""}
                {searchQuery && ` matching "${searchQuery}"`}
                {selectedStatuses.length > 0 &&
                  ` with status: ${selectedStatuses.map((s) => s.replace("_", " ")).join(", ")}`}
              </div>
            )}

            {/* Search input */}
            <div className="relative">
              <input
                className="input pl-10 w-full"
                placeholder="Search tickets..."
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            {/* Status filter pills */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter by Status:
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                {statusOptions.map((status) => (
                  <button
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      selectedStatuses.includes(status.value)
                        ? `${status.color} text-white shadow-md`
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                    key={status.value}
                    type="button"
                    onClick={() => toggleStatus(status.value)}
                  >
                    {selectedStatuses.includes(status.value) && (
                      <i className="fas fa-check mr-2" />
                    )}
                    {status.label}
                  </button>
                ))}
                {/* Admin-only filter: Created by Admins */}
                {user?.role === "admin" && (
                  <button
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      showCreatedByAdmin
                        ? "bg-purple-500 hover:bg-purple-600 text-white shadow-md"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                    }`}
                    type="button"
                    onClick={() => setShowCreatedByAdmin(!showCreatedByAdmin)}
                  >
                    {showCreatedByAdmin && <i className="fas fa-check mr-2" />}
                    Created by Admins
                  </button>
                )}
                {(searchQuery ||
                  selectedStatuses.length > 0 ||
                  registrationIdFilter ||
                  showCreatedByAdmin) && (
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedStatuses(
                        user?.role === "admin"
                          ? ["open", "customer_replied"]
                          : []
                      );
                      setShowCreatedByAdmin(false);
                      if (registrationIdFilter) {
                        setRegistrationIdFilter("");
                        navigate("/tickets");
                      }
                    }}
                  >
                    <i className="fas fa-times mr-2" />
                    <span>Clear Filters</span>
                  </button>
                )}
              </div>
              {selectedStatuses.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedStatuses.length} status
                  {selectedStatuses.length !== 1 ? "es" : ""} selected (click to
                  toggle)
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="table-header px-2 sm:px-4 py-3 whitespace-nowrap w-auto sm:w-[5%]">
                  <input
                    aria-label="Select all visible tickets"
                    checked={allVisibleSelected}
                    className="rounded"
                    type="checkbox"
                    onChange={toggleAllVisible}
                  />
                </th>
                <th
                  className="table-header px-2 sm:px-4 py-3 whitespace-nowrap text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-auto sm:w-[10%]"
                  onClick={() => handleSort("ticketNumber")}
                >
                  <div className="flex items-center gap-1">
                    ID
                    {sortField === "ticketNumber" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                <th
                  className="table-header px-2 sm:px-4 py-3 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-auto sm:w-[25%]"
                  onClick={() => handleSort("subject")}
                >
                  <div className="flex items-center gap-1">
                    Subject
                    {sortField === "subject" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                {user?.role === "admin" && (
                  <th className="table-header px-2 sm:px-4 py-3 text-left w-auto sm:w-[15%]">
                    Customer
                  </th>
                )}
                <th className="table-header px-2 sm:px-4 py-3 text-center w-auto sm:w-[10%]">
                  Replies
                </th>
                <th
                  className="table-header px-2 sm:px-4 py-3 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-auto sm:w-[10%]"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortField === "status" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
                {user?.role === "admin" && (
                  <th
                    className="table-header px-2 sm:px-4 py-3 text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-auto sm:w-[15%]"
                    onClick={() => handleSort("assignedTo")}
                  >
                    <div className="flex items-center gap-1">
                      Assigned To
                      {sortField === "assignedTo" && (
                        <i
                          className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                        />
                      )}
                    </div>
                  </th>
                )}
                <th
                  className="table-header px-2 sm:px-4 py-3 whitespace-nowrap text-left cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-auto sm:w-[15%]"
                  onClick={() => handleSort("updatedAt")}
                >
                  <div className="flex items-center gap-1">
                    Last Updated
                    {sortField === "updatedAt" && (
                      <i
                        className={`fas fa-chevron-${sortDirection === "asc" ? "up" : "down"} text-xs text-primary-600`}
                      />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr
                    className="border-b border-gray-100 dark:border-gray-700"
                    key={idx}
                  >
                    {Array.from({
                      length: user?.role === "admin" ? 7 : 6,
                    }).map((__, cellIdx) => (
                      <td className="px-3 py-3" key={cellIdx}>
                        <Shimmer className="h-3.5 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : tickets.length === 0 ? (
                // Show empty state
                <tr>
                  <td
                    className="text-center py-8 text-gray-500"
                    colSpan={user?.role === "admin" ? "7" : "6"}
                  >
                    No tickets found
                  </td>
                </tr>
              ) : (
                // Show actual tickets (sorted)
                sortedTickets.map((ticket, index) => {
                  const ticketId = ticket.ticket_id || ticket.id;
                  const ticketNumber =
                    ticket.ticket_number || ticket.ticketNumber || ticket.id;
                  const customerName =
                    typeof ticket.customer === "object" && ticket.customer
                      ? ticket.customer.name
                      : ticket.customer || "Unknown";

                  return (
                    <motion.tr
                      animate={{ opacity: 1, y: 0 }}
                      className={`border-b border-gray-100 dark:border-gray-700 cursor-pointer ${
                        index % 2 === 0
                          ? "table-row-striped-even"
                          : "table-row-striped-odd"
                      } table-row-hover`}
                      initial={{ opacity: 0, y: 20 }}
                      key={ticketId}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleTicketClick(ticketNumber)}
                    >
                      <td className="table-cell px-2 sm:px-4 py-3">
                        <input
                          aria-label={`Select ticket ${ticketNumber}`}
                          checked={selectedTickets.has(ticketNumber)}
                          className="rounded"
                          type="checkbox"
                          onChange={() => toggleTicket(ticketNumber)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="table-cell px-2 sm:px-4 py-3 font-medium text-primary-600 whitespace-nowrap">
                        {ticketNumber}
                      </td>
                      <td className="table-cell px-2 sm:px-4 py-3">
                        <div className="max-w-xs">
                          <p className="font-medium truncate">
                            {ticket.subject}
                          </p>
                          {ticket.comments && ticket.comments.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <i className="fas fa-reply text-xs text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {ticket.comments.length}{" "}
                                {ticket.comments.length === 1
                                  ? "reply"
                                  : "replies"}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      {user?.role === "admin" && (
                        <td className="table-cell px-4 py-3 hidden sm:table-cell">
                          <div className="flex items-center gap-1">
                            {ticket.customerAvatarUrl ? (
                              <img
                                alt={customerName}
                                className="w-8 h-8 rounded-full object-cover"
                                src={ticket.customerAvatarUrl}
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                {customerName ? customerName.charAt(0) : "?"}
                              </div>
                            )}
                            <span className="truncate max-w-[150px]">
                              {customerName || "Unknown"}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="table-cell px-4 py-3 hidden sm:table-cell text-center">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {ticket.commentCount || 0}
                        </span>
                      </td>
                      <td className="table-cell px-2 sm:px-4 py-3 text-center">
                        <StatusBadge status={ticket.status} />
                      </td>
                      {user?.role === "admin" && (
                        <td className="table-cell px-4 py-3 hidden sm:table-cell">
                          {ticket.assignedToObject &&
                          ticket.assignedToObject.name !== "Unassigned" ? (
                            <div className="flex items-center gap-2">
                              {ticket.assignedToAvatarUrl ? (
                                <img
                                  alt={ticket.assignedToObject.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                  src={ticket.assignedToAvatarUrl}
                                />
                              ) : (
                                <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {ticket.assignedToObject.name
                                    ? ticket.assignedToObject.name
                                        .charAt(0)
                                        .toUpperCase()
                                    : "?"}
                                </div>
                              )}
                              <span className="truncate max-w-[120px] text-sm">
                                {ticket.assignedToObject.name ||
                                  ticket.assignedToObject.email}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">
                              Unassigned
                            </span>
                          )}
                        </td>
                      )}
                      <td className="table-cell px-4 py-3 text-gray-500 whitespace-nowrap hidden sm:table-cell">
                        <div className="flex flex-col">
                          <span
                            className={`${(() => {
                              const updateTime = new Date(
                                ticket.lastReplyTime ||
                                  ticket.updatedAt ||
                                  ticket.createdAt
                              );
                              const now = new Date();
                              const diffMinutes =
                                (now - updateTime) / (1000 * 60);

                              if (diffMinutes <= 30) {
                                return "text-green-600 dark:text-green-400 font-medium";
                              } else if (diffMinutes > 24 * 60) {
                                return "text-red-600 dark:text-red-400 font-medium";
                              }

                              return "";
                            })()}`}
                          >
                            {getRelativeTime(
                              ticket.lastReplyTime ||
                                ticket.updatedAt ||
                                ticket.createdAt
                            )}
                          </span>
                          {(ticket.updatedAt &&
                            ticket.updatedAt !== ticket.createdAt) ||
                          (ticket.lastReplyTime &&
                            ticket.lastReplyTime !== ticket.createdAt) ? (
                            <span className="text-xs text-gray-400">
                              {ticket.lastReplyBy?.role === "admin"
                                ? "Admin reply"
                                : ticket.lastReplyBy?.role
                                  ? "Customer reply"
                                  : "Updated"}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">
                              No replies yet
                            </span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="w-full p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {pagination.total === 0
                  ? "No entries to display"
                  : `Showing ${(pagination.page - 1) * pagination.limit + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} entries`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Show:
                </span>
                <select
                  className="input py-1 px-2 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                .filter(
                  (page) =>
                    page === 1 ||
                    page === pagination.pages ||
                    Math.abs(page - currentPage) <= 1
                )
                .map((page, index, array) => {
                  const showEllipsisBefore =
                    index > 0 && array[index - 1] !== page - 1;

                  return (
                    <React.Fragment key={page}>
                      {showEllipsisBefore && (
                        <span className="px-3 py-1 text-sm text-gray-500">
                          ...
                        </span>
                      )}
                      <button
                        className={`px-3 py-1 text-sm rounded-lg ${
                          currentPage === page
                            ? "bg-primary-600 text-white"
                            : "border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              <button
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={currentPage === pagination.pages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* New Ticket Modal */}
      <NewTicketModal
        initialSubject={newTicketInitialSubject}
        isOpen={showNewTicketModal}
        onClose={handleCloseNewTicketModal}
        onTicketCreated={handleTicketCreated}
      />

      {/* Bulk Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => !deleting && setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-trash text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold mb-1">
                  Delete {selectedTickets.size} ticket
                  {selectedTickets.size === 1 ? "" : "s"}?
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This permanently deletes the selected ticket
                  {selectedTickets.size === 1 ? "" : "s"} and all attached
                  comments. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                disabled={deleting}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-60"
                disabled={deleting}
                onClick={handleBulkDelete}
              >
                {deleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
